import type { FormEvent } from "react";
import { Alerta, Adjuntos, Button, MontoField, Row, Select, Stack, TextField, type OpcionSelect } from "@/components";
import type { Adjunto, CambioMoneda, SentidoCambio } from "@/lib/api";
import { OPCIONES_SENTIDO, tipoCambioCalculado, type ErroresCambio, type FormCambio } from "@/lib/caja";

export interface FormularioCambioProps {
  form: FormCambio;
  cambiar: <K extends keyof FormCambio>(campo: K, valor: FormCambio[K]) => void;
  errores: ErroresCambio;
  onSubmit: (e: FormEvent) => void;
  onCancelar: () => void;
  guardando: boolean;
  error: string | null;
  /** null = alta (todavía no hay adjuntos que mostrar). En edición, el comprobante contra /caja/cambios/{id}/adjuntos. */
  cambio: CambioMoneda | null;
  rutaDescarga: (a: Adjunto) => string;
  onSubirAdjunto: (archivo: File) => Promise<void>;
  onBorrarAdjunto: (a: Adjunto) => Promise<void>;
  subiendoAdjunto: boolean;
  errorAdjuntos: string | null;
  /** Cajas para elegir; null = una sola caja activa (no se pregunta: va a la principal). */
  opcionesCaja: OpcionSelect[] | null;
}

/** Campos de un cambio de moneda (alta y edición). Sin `<Dialog>`: lo envuelve `CajaTab`. */
export function FormularioCambio({
  form,
  cambiar,
  errores,
  onSubmit,
  onCancelar,
  guardando,
  error,
  cambio,
  rutaDescarga,
  onSubirAdjunto,
  onBorrarAdjunto,
  subiendoAdjunto,
  errorAdjuntos,
  opcionesCaja,
}: FormularioCambioProps) {
  const calculado = tipoCambioCalculado(form);
  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack espacio={4}>
        <Select etiqueta="Sentido" opciones={OPCIONES_SENTIDO} valor={form.sentido} onCambio={(v) => cambiar("sentido", v as SentidoCambio)} error={errores.sentido} />
        <TextField etiqueta="Fecha" type="date" value={form.fecha} onChange={(e) => cambiar("fecha", e.target.value)} error={errores.fecha} />
        <MontoField etiqueta="Monto en dólares" valor={form.monto_usd} onCambio={(v) => cambiar("monto_usd", v)} moneda="USD" error={errores.monto_usd} />
        <MontoField etiqueta="Monto en pesos" valor={form.monto_ars} onCambio={(v) => cambiar("monto_ars", v)} moneda="ARS" error={errores.monto_ars} />
        <p className="text-chico text-texto-suave">{`Tipo de cambio: ${calculado ?? "—"}`}</p>
        {opcionesCaja && <Select etiqueta="Caja" opciones={opcionesCaja} valor={form.caja_id} onCambio={(v) => cambiar("caja_id", v)} placeholder="Elegí la caja" />}
        <TextField etiqueta="Nota" ayuda="Opcional" value={form.nota} onChange={(e) => cambiar("nota", e.target.value)} error={errores.nota} />
        {cambio && (
          <Adjuntos
            etiqueta="Comprobante"
            adjuntos={cambio.adjuntos}
            rutaDescarga={rutaDescarga}
            puedeEditar
            onSubir={onSubirAdjunto}
            onBorrar={onBorrarAdjunto}
            subiendo={subiendoAdjunto}
            error={errorAdjuntos}
          />
        )}
        <Alerta texto={error} />
        <Row justificar="fin" espacio={2}>
          <Button variante="secundario" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit" cargando={guardando}>
            Guardar
          </Button>
        </Row>
      </Stack>
    </form>
  );
}
